import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { RoomAmenitiesMapping, syncRoomAmenities } from '~/services/AccommodationService';

interface SyncRoomAmenitiesParams {
  reservationPropertyId: string;
  reservationRoomId: string;
  suppressNotifications?: boolean;
}

interface UseSyncRoomAmenitiesOptions {
  onSuccess?: (amenities: RoomAmenitiesMapping['amenities']) => void;
}

export function useSyncRoomAmenities({ onSuccess }: UseSyncRoomAmenitiesOptions = {}) {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ reservationPropertyId, reservationRoomId }: SyncRoomAmenitiesParams) => {
      return syncRoomAmenities({ reservationPropertyId, reservationRoomId });
    },
    onSuccess: (response, variables) => {
      if (response.result.amenities.length > 0) {
        onSuccess?.(response.result.amenities);
        if (!variables.suppressNotifications) {
          enqueueSnackbar(`Successfully synced ${response.result.amenities.length} room amenities`, {
            variant: 'success',
          });
        }
      } else {
        if (!variables.suppressNotifications) {
          enqueueSnackbar(response.result.message || 'No room amenities were found to sync', { variant: 'warning' });
        }
      }
    },
    onError: (error: Error, variables) => {
      if (!variables.suppressNotifications) {
        enqueueSnackbar(`Failed to sync room amenities: ${error.message}`, { variant: 'error' });
      }
    },
  });
}
